/**
 * Hands the platform's runway and taxiway closures to the boards.
 *
 * Renders nothing and sits in the shell, so the NOTAM board, the instruments
 * and the executive figures read the register even when the map itself never
 * opens. Platform only: under the demonstration the map keeps its own list
 * and its two sample closures, exactly as before.
 *
 * It costs nothing until it is needed: the read starts only once the boards'
 * engine — the only reader of what it fills — has loaded, and it never causes
 * that load.
 */
import { useEffect, useState } from "react";
import { useSession } from "../../../app/SessionContext";
import { useMapClosures } from "../../../api/closuresQueries";
import { DataSourceError } from "../../../api/dataSource";
import { demoIfLoaded, whenDemoLoaded } from "../../domain/load";
import { setClosuresFeed } from "./closuresStatus";

/** The merge, and the tables it reads, fetched only when there is something to merge. */
const merger = () => import("../mergeClosures");

export function ClosuresFeed() {
  const { source, site } = useSession();
  const live = source.kind === "platform";
  const [engine, setEngine] = useState(() => demoIfLoaded() !== null);
  useEffect(() => {
    if (!engine) whenDemoLoaded(() => setEngine(true));
  }, [engine]);

  const q = useMapClosures({ enabled: live && engine });
  const data = q.data;
  const error = q.error;

  /* the register holds the closures from the moment the engine is here —
     queued, so it runs as the engine arrives and before any board first
     draws: this browser's old list and the samples never show, even once */
  useEffect(() => {
    if (!live) {
      setClosuresFeed(null);
      return;
    }
    whenDemoLoaded((d) => d.fedMapClosures());
  }, [live, site.id]);

  useEffect(() => {
    if (!live || !engine || !data) return;
    void merger().then((m) => m.mergeClosures(data));
  }, [live, engine, data]);

  useEffect(() => {
    if (!live || !engine || !error) return;
    const status = error instanceof DataSourceError ? error.status : undefined;
    void merger().then((m) =>
      status === 401 || status === 403 ? m.mergeClosures([], error.message) : m.closuresUnread(error.message),
    );
  }, [live, engine, error]);

  return null;
}
